import type { ITenantDestination } from '@/entities/tenant-destination'
import type { PoiCategoryOption } from '@/features/poi/poi-basic-info-form'
import type { PoiBasicInfoState, PoiType } from '@/shared/api/types/poi-types'
import { useTranslation } from 'react-i18next'
import { PoiBasicInfoForm } from '@/features/poi/poi-basic-info-form'
import { cn } from '@/shared/libs/cn'
import { FormSectionCard } from '@/shared/ui/form-section-card'
import { PublishStatusSwitch } from '@/shared/ui/publish-status-switch'
import { SectionSaveButton } from '@/shared/ui/section-save-button'
import { DestinationSelectField } from './destination-select-field'

interface CategoriesQueryState {
  isLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

interface BasicInfoCardProps {
  isEdit: boolean
  isFormReady: boolean
  tenantId: string
  languageCode: string
  basicInfo: PoiBasicInfoState
  onBasicInfoChange: (value: PoiBasicInfoState) => void
  tenantDestinationId: string
  onDestinationChange: (destinationId: string) => void
  destinations: ITenantDestination[]
  isDestinationsLoading: boolean
  selectedDestination: ITenantDestination | undefined
  categoryOptions: PoiCategoryOption[]
  typeOptions: PoiType[]
  categoriesQuery: CategoriesQueryState
  onCreateDestination: () => void
  onCreateCategory: () => void
  /** Publish state; only chosen when creating (an existing POI is (de)activated from the list). */
  isActive: boolean
  onActiveChange: (value: boolean) => void
  isDirty: boolean
  isSaving: boolean
  isSaveBlocked: boolean
  onSave: () => void
}

/** Destination, category, type, popup-ad flag, radius and (on create) the publish switch. */
export function BasicInfoCard({
  isEdit,
  isFormReady,
  tenantId,
  languageCode,
  basicInfo,
  onBasicInfoChange,
  tenantDestinationId,
  onDestinationChange,
  destinations,
  isDestinationsLoading,
  selectedDestination,
  categoryOptions,
  typeOptions,
  categoriesQuery,
  onCreateDestination,
  onCreateCategory,
  isActive,
  onActiveChange,
  isDirty,
  isSaving,
  isSaveBlocked,
  onSave,
}: BasicInfoCardProps) {
  const { t } = useTranslation()

  return (
    <FormSectionCard
      title={t('poi.basicInfo')}
      action={
        isEdit && (
          <SectionSaveButton
            disabled={isSaveBlocked || !isDirty}
            saving={isSaving}
            onSave={onSave}
          />
        )
      }
    >
      <div className={cn(!isFormReady && 'opacity-50 pointer-events-none')}>
        <DestinationSelectField
          value={tenantDestinationId}
          onChange={onDestinationChange}
          destinations={destinations}
          isLoading={isDestinationsLoading}
          disabled={!tenantId || isDestinationsLoading}
          languageCode={languageCode}
          selectedDestination={selectedDestination}
          onCreateDestination={onCreateDestination}
        />
        <PoiBasicInfoForm
          value={basicInfo}
          categories={categoryOptions}
          types={typeOptions}
          onChange={onBasicInfoChange}
          languageCode={languageCode}
          isCategoriesLoading={categoriesQuery.isLoading}
          hasCategoriesNextPage={categoriesQuery.hasNextPage}
          isFetchingCategoriesNextPage={categoriesQuery.isFetchingNextPage}
          onLoadMoreCategories={() => void categoriesQuery.fetchNextPage()}
          onCreateCategory={onCreateCategory}
        />
        {!isEdit && <PublishStatusSwitch checked={isActive} onCheckedChange={onActiveChange} />}
      </div>
    </FormSectionCard>
  )
}
